/**
 * The Pro card's code row (subscription R19): the code on the card, the field it is typed into,
 * the message a refused code comes back with, and where focus goes after each of them.
 *
 * It decides nothing about the purchase. Which face the row wears and what the checkout carries is
 * `model/trialCode`'s; why a checkout was refused is `model/webCheckout`'s. This file holds the four
 * pieces of local state those decisions read, and the focus moves the approved spec asks for.
 *
 * ── Focus moves in the same task as the state that makes its target exist ───────────────────
 * „Usuń" does not exist until the code is applied, and the field does not exist until it opens, so
 * focusing either from the handler would focus nothing. `flushSync` commits the state first and the
 * focus follows on the element that commit drew — no effect watching a value, and no timer.
 */

import { useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';

import { normalizeTrialCode } from '../model/trialCode';

export interface TrialCodeApi {
  /** The code applied on the card, or `null`. Local only — the checkout is what judges it. */
  readonly applied: string | null;
  /** Whether the field is open. True also while a refused code stands in it with its message. */
  readonly editing: boolean;
  readonly draft: string;
  /** Why the server refused the code, under the field. Cleared by the next keystroke. */
  readonly error: string | null;
  readonly toggleRef: RefObject<HTMLButtonElement | null>;
  readonly fieldRef: RefObject<HTMLInputElement | null>;
  readonly removeRef: RefObject<HTMLButtonElement | null>;
  /** „Masz kod?" — opens the field and puts the caret in it. */
  readonly onOpen: () => void;
  readonly onDraft: (value: string) => void;
  /** „Zastosuj", and Enter in the field. Nothing happens with an empty field. */
  readonly onApply: () => void;
  /** Escape in the field: an empty field closes, a filled one is cleared. */
  readonly onEscape: () => void;
  /** „Usuń" — the card goes back to „Masz kod?". */
  readonly onRemove: () => void;
  /**
   * The card's button was pressed with a code typed and not applied (`model/trialCode`'s
   * `trialCodeAtPress`): the code goes on the card. Focus stays on the button that was pressed.
   */
  readonly onAppliedByPress: (code: string) => void;
  /**
   * The checkout refused the CODE it carried: back to the field, with that code in it and the
   * reason in the card's fact line.
   */
  readonly onRefused: (code: string, message: string) => void;
}

/**
 * @param linked the code a link brought (`?code=`), already normalised, or `null`. Read once, on
 *   mount, the way the checkout's own return parameters are.
 */
export function useTrialCode(linked: string | null): TrialCodeApi {
  const [applied, setApplied] = useState<string | null>(linked);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const removeRef = useRef<HTMLButtonElement | null>(null);

  const closeToToggle = () => {
    flushSync(() => {
      setEditing(false);
      setDraft('');
      setError(null);
    });
    toggleRef.current?.focus();
  };

  return {
    applied,
    editing,
    draft,
    error,
    toggleRef,
    fieldRef,
    removeRef,
    onOpen: () => {
      flushSync(() => {
        setEditing(true);
      });
      fieldRef.current?.focus();
    },
    // Capitals as the trainer types, and no whitespace at all: a code has none, and a pasted one
    // often carries a trailing space that would otherwise be the reason it is „unknown".
    onDraft: (value) => {
      setDraft(value.replace(/\s/gu, '').toUpperCase());
      setError(null);
    },
    onApply: () => {
      const code = normalizeTrialCode(draft);
      if (code === null) return;
      flushSync(() => {
        setApplied(code);
        setEditing(false);
        setDraft('');
        setError(null);
      });
      removeRef.current?.focus();
    },
    onEscape: () => {
      if (draft === '') {
        closeToToggle();
        return;
      }
      setDraft('');
      setError(null);
    },
    onRemove: () => {
      setApplied(null);
      closeToToggle();
    },
    onAppliedByPress: (code) => {
      setApplied(code);
      setEditing(false);
      setDraft('');
      setError(null);
    },
    onRefused: (code, message) => {
      flushSync(() => {
        setApplied(null);
        setEditing(true);
        setDraft(code);
        setError(message);
      });
      fieldRef.current?.focus();
      fieldRef.current?.select();
    },
  };
}
